import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle, IconInfoCircle } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";

interface EditPointsModalProps {
  opened: boolean;
  onClose: () => void;
  clientId: string;
  /** NUMERIC business_locations.id (backend rejects the uuid with 400). */
  businessLocationId: number;
  /** Current points balance shown read-only ("Total points" variant). */
  currentPoints: number;
  /** Called with the POST /adjust response so the caller can refresh the
   *  account block without refetching. */
  onSaved: (response: any) => void;
}

/**
 * Edit Points modal (loyalty FE1 §4.1) — plain "Total points" variant (the
 * tier-bar variant is a tiers-era layout). Saves a manual delta via
 * POST /clients/{id}/loyalty/adjust {business_location_id, delta, note}.
 */
export default function EditPointsModal({
  opened,
  onClose,
  clientId,
  businessLocationId,
  currentPoints,
  onSaved,
}: EditPointsModalProps) {
  const { adjustClientLoyaltyPoints } = useApp();
  const [delta, setDelta] = useState<number | string>(0);
  const [note, setNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setDelta(0);
      setNote("");
      setError(null);
    }
  }, [opened]);

  const deltaNumber = typeof delta === "number" ? delta : parseInt(delta, 10);
  const deltaValid = Number.isFinite(deltaNumber) && deltaNumber !== 0;
  // A deduction can't take the balance below zero.
  const resultingBalance = currentPoints + (deltaValid ? deltaNumber : 0);
  // Backend requires a non-empty note on every manual adjustment.
  const noteValid = note.trim().length > 0;
  const canSave = deltaValid && noteValid && resultingBalance >= 0 && !isSaving;

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setError(null);
    try {
      const response = await adjustClientLoyaltyPoints(clientId, {
        business_location_id: businessLocationId,
        delta: deltaNumber,
        note: note.trim(),
      });
      onSaved(response);
      onClose();
    } catch (err: any) {
      console.error("Error adjusting loyalty points:", err);
      // Only trust messages parsed from a JSON error body (err.errorBody);
      // for non-JSON responses fetchWithAuth sets the generic
      // "Failed to fetch data" message.
      const bodyMessage =
        err?.errorBody && typeof err.errorBody === "object"
          ? err.errorBody.message || err.errorBody.error
          : null;
      const serverMessage =
        err?.status >= 400 &&
        err?.status < 500 &&
        typeof bodyMessage === "string" &&
        bodyMessage.trim()
          ? bodyMessage
          : "Could not save the points adjustment. Please try again.";
      setError(serverMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={isSaving ? () => {} : onClose}
      title={<Title order={4}>Add / Subtract Points</Title>}
      size="lg"
    >
      <Stack gap="md">
        <Group grow align="flex-start">
          <TextInput
            label="Total points"
            value={`${currentPoints.toLocaleString("en-US")} points`}
            readOnly
            disabled
          />
          <NumberInput
            label={
              <Group gap={4} wrap="nowrap">
                <Text size="sm" fw={500} component="span">
                  Add / Subtract Points
                </Text>
                <Tooltip
                  label="Add or subtract points from this client's balance. Use a negative number to deduct."
                  withArrow
                >
                  <IconInfoCircle size={14} />
                </Tooltip>
              </Group>
            }
            value={delta}
            onChange={setDelta}
            clampBehavior="none"
            allowDecimal={false}
            step={1}
          />
        </Group>

        <Textarea
          label="Notes"
          withAsterisk
          placeholder="Reason for the adjustment (required)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          minRows={3}
          autosize
        />

        {deltaValid && resultingBalance < 0 && (
          <Text size="xs" c="red">
            This adjustment would take the balance below zero.
          </Text>
        )}

        {error && (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertCircle size={18} />}
          >
            {error}
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={isSaving}>
            Back
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSave}
            loading={isSaving}
            disabled={!canSave}
          >
            Save Updates
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
